import { fetchUserModules, fetchVisitedModules, UserModule } from './api/methodsApi';
import { sortModules } from './utils';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  withScrollThreshold,
  WithScrollThresholdProps,
} from '../../shared-components/elementis/hoc/withScrollThreshold';
import { buildGroupProps } from '../../utils/elementisUtils';
import { MethodList } from './components/MethodList';
import { TABS } from '.';
import AddLicenseModal from '../../shared-components/AddLicenseModal';
import ContentBody from '../../layout/content/ContentBody';
import ContentHeader from '../../layout/content/ContentHeader';
import EmptyState from './components/EmptyState';
import IdentityContext from '../../contexts/IdentityContext';
import ModalContext from '../../contexts/ModalContext';
import Role from '../../core/security/role';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import useGroupInviteToasts from '../my-teachers/hooks/useGroupInviteToasts';
import UserRoleAlert from './components/UserRoleAlert';
import useThrowAsyncError from '../../hooks/useThrowAsyncError';
import { useMarketingNotifications } from '../../shared-components/marketing-notification';

import Button from '@pelckmans/elementis/components/button';
import Plus from '@pelckmans/elementis/icons/plus';
import Tabs from '@pelckmans/elementis/components/tabs';
import Page from '@pelckmans/elementis/components/page';

interface DashboardProps extends WithScrollThresholdProps {}

function Dashboard({ pastThreshold }: DashboardProps) {
  const { REACT_APP_P_STUDIO_GROUP } = process.env;
  const {
    identity: { isTeacherAlike, highestRole },
  } = useContext(IdentityContext);
  const isVoStudent = REACT_APP_P_STUDIO_GROUP === 'VO' && highestRole === Role.STUDENT;
  const [isLoading, setIsLoading] = useState(true);
  const [groupedModules, setGroupedModules] = useState<{ years: number[]; modules: UserModule[] }[]>([]);
  const [phasedOutModules, setPhasedOutModules] = useState<{ years: number[]; modules: UserModule[] }[]>([]);
  const [visitedModules, setVisitedModuleIds] = useState<string[]>([]);
  const [addedModuleIds, setAddedModuleIds] = useState<string[]>([]);
  const [userModules, setUserModules] = useState<UserModule[]>([]);

  const { showModal } = useContext(ModalContext);
  const { t } = useTranslation();
  const handleError = useThrowAsyncError();

  useDocumentTitle(t('PAGES.MY_METHODS.DASHBOARD.DOCUMENT_TITLE'));
  useMarketingNotifications();

  useGroupInviteToasts();

  const fetchModules = useCallback(async () => {
    const fetchedUserModules = await fetchUserModules(REACT_APP_P_STUDIO_GROUP);

    setUserModules(fetchedUserModules);

    try {
      if (highestRole === Role.STUDENT) {
        setGroupedModules(sortModules(fetchedUserModules, false));
        setIsLoading(false);
        return fetchedUserModules;
      }

      const { phasedOut, others } = fetchedUserModules.reduce<{
        phasedOut: UserModule[];
        others: UserModule[];
      }>(
        (acc, item) => {
          (item.publishingState === 'phasedOut' ? acc.phasedOut : acc.others).push(item);
          return acc;
        },
        { phasedOut: [], others: [] },
      );

      setGroupedModules(sortModules(others, isTeacherAlike));
      setPhasedOutModules(sortModules(phasedOut, isTeacherAlike));

      return fetchedUserModules;
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }, [REACT_APP_P_STUDIO_GROUP, highestRole, isTeacherAlike, handleError]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  useEffect(() => {
    async function fetchVisited() {
      const modules = await fetchVisitedModules();

      setVisitedModuleIds(modules);
    }
    fetchVisited().catch(handleError);
  }, [handleError]);

  const refetchAfterModuleAdded = async () => {
    const oldModulesMap = new Map(userModules.map(m => [m.id, m]));

    const newModules = await fetchModules();
    const newIds: string[] = [];

    if (!newModules) return;

    for (const m of newModules) {
      const oldModule = oldModulesMap.get(m.id);
      if (!oldModule) {
        newIds.push(m.id);
      } else if (m.subModules.length > oldModule.subModules.length) {
        newIds.push(m.id);
      }
    }

    setAddedModuleIds(newIds);
  };

  const showAddLicenseModal = () =>
    showModal(<AddLicenseModal id="add-license-modal" onRefetch={refetchAfterModuleAdded} />);

  if (isVoStudent) {
    return (
      <>
        <ContentHeader
          cover={
            <Page.Header
              headerText={t('PAGES.MY_METHODS.HEADER.TITLE')}
              density={pastThreshold ? 'compact' : 'normal'}
            />
          }
        />
        <ContentBody>
          {groupedModules && (
            <MethodList
              phasedOutModules={phasedOutModules}
              groupedModules={groupedModules}
              visitedModules={visitedModules}
              addedModuleIds={addedModuleIds}
            />
          )}
        </ContentBody>
      </>
    );
  }

  return (
    <>
      <ContentHeader
        cover={
          <Page.Header
            density={pastThreshold ? 'compact' : 'normal'}
            headerText={t('PAGES.MY_METHODS.HEADER.TITLE')}
            tabs={
              <Tabs.TabList aria-label={t('PAGES.MY_METHODS.HEADER.TAB_LIST.A11Y_LABEL')}>
                <Tabs.Tab id={TABS.DASHBOARD}>{t('PAGES.MY_METHODS.HEADER.TAB_LIST.TABS.MY_METHODS')}</Tabs.Tab>
                <Tabs.Tab id={TABS.CATALOGUE}>{t('PAGES.MY_METHODS.HEADER.TAB_LIST.TABS.CATALOGUE')}</Tabs.Tab>
              </Tabs.TabList>
            }
            buttonGroupProps={buildGroupProps(
              <Button startIcon={<Plus />} prominence="secondary" onClick={showAddLicenseModal}>
                {t('PAGES.MY_METHODS.HEADER.BUTTONS_GROUP.ACTIVATE_LICENSE')}
              </Button>,
            )}
          />
        }
      />
      <ContentBody alerts={<UserRoleAlert />}>
        <Tabs.TabsContent>
          <Tabs.TabPanel id={TABS.DASHBOARD}>
            {groupedModules.length === 0 && phasedOutModules.length === 0 ? (
              <EmptyState
                isLoading={isLoading}
                onRefetch={fetchModules}
                aria-label={t('PAGES.MY_METHODS.LOADING_STATES.METHOD')}
              />
            ) : (
              <MethodList
                phasedOutModules={phasedOutModules}
                groupedModules={groupedModules}
                visitedModules={visitedModules}
                addedModuleIds={addedModuleIds}
              />
            )}
          </Tabs.TabPanel>
        </Tabs.TabsContent>
      </ContentBody>
    </>
  );
}

export default withScrollThreshold(Dashboard);
